import { type CollectionDto, MAX_COLLECTIONS_PER_USER } from "@serophin/nottldr-shared";
import { useCollections } from "@serophin/nottldr-shared/hooks";
import { Loader2, Plus } from "lucide-preact";
import { useState } from "preact/hooks";
import { useToast } from "../../contexts/ToastContext";
import { CollectionCard } from "./CollectionCard";
import { COLLECTION_LIMIT_MESSAGE, errorMessage } from "./collectionText";

const useCreate = (collections: CollectionDto[]) => {
  const toast = useToast();
  const { create } = useCollections();
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  const submit = async (event: Event) => {
    event.preventDefault();
    if (!name.trim()) {
      return;
    }
    if (collections.length >= MAX_COLLECTIONS_PER_USER) {
      toast.error(COLLECTION_LIMIT_MESSAGE);
      return;
    }
    setCreating(true);
    try {
      await create(name.trim());
      setName("");
      toast.success("Collection created successfully!");
    } catch (error) {
      toast.error(errorMessage(error, "Failed to create collection"));
    } finally {
      setCreating(false);
    }
  };

  return { name, setName, creating, submit };
};

const INPUT =
  "flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-neutral-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50";
const SUBMIT =
  "px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center gap-1.5";

export const ManageCollections = ({ collections }: { collections: CollectionDto[] }) => {
  const { name, setName, creating, submit } = useCreate(collections);

  return (
    <div class="max-w-2xl mx-auto">
      <form onSubmit={submit} class="mb-3">
        <div class="flex gap-2">
          <input
            type="text"
            value={name}
            onInput={(event) => setName(event.currentTarget.value)}
            placeholder="New collection name..."
            disabled={creating}
            class={INPUT}
            maxLength={30}
          />
          <button type="submit" disabled={creating || !name.trim()} class={SUBMIT}>
            {creating ? <Loader2 size={16} class="animate-spin" /> : <Plus size={16} />}
            {creating ? "Creating..." : "Create"}
          </button>
        </div>
      </form>
      <div class="space-y-2">
        {collections.map((collection) => (
          <CollectionCard key={collection.id} collection={collection} allCollections={collections} />
        ))}
      </div>
    </div>
  );
};
